import Constants from '@/Constants';
import { useApiMutation, useApiQuery } from '@/hooks/useApi';
import { GetCertificatesResponse, makeRequest } from '@/services/api';
import { createKey, getSessionItem } from '@/utils/helper';

export type ActivityType = 'NATIVE' | 'NON_NATIVE';

interface ActivityMetadata {
  image: string;
  redirectUrl: string;
}

interface ActivityStats {
  modulesCompleted: number;
  totalModules: number;
}

export interface Activity {
  id: string;
  name: string;
  code: string;
  type: ActivityType;
  description: string;
  metadata: ActivityMetadata;
  navigationType?: string;
  stats?: ActivityStats;
  certificate?: string | null;
}
export interface GetActivitiesResponse {
  activityList: Activity[];
}

const getCommonHeaders = () => ({
  'session-id': getSessionItem('sessionId'),
  'student-id': getSessionItem('studentId'),
});

interface InitializeActivityResponse {
  accessCode: string;
  expireAt: string;
}

interface ActivityStatsPayload {
  activityId: string;
  startTime: string;
  endTime: string;
  duration: number;
}

export const useInitializeActivity = () =>
  useApiMutation({
    mutationFn: ({ activityId }: { activityId: string }) => {
      const accessToken = localStorage.getItem('accessToken');
      const headers = {
        ...getCommonHeaders(),
        Authorization: `Bearer ${accessToken}`,
      };
      return makeRequest<InitializeActivityResponse>(
        Constants.INITIALIZE_ACTIVITY_URL,
        { activityId },
        headers,
      );
    },
  });

export const useSubmitActivityStats = () =>
  useApiMutation({
    mutationFn: ({ activityId, startTime, endTime, duration }: ActivityStatsPayload) => {
      const accessToken = localStorage.getItem('accessToken');
      const headers = {
        ...getCommonHeaders(),
        Authorization: `Bearer ${accessToken}`,
      };
      return makeRequest(
        Constants.ACTIVITY_STATS_BASE_URL,
        { activityId, startTime, endTime, duration },
        headers,
      );
    },
  });

export type ActivityModuleStatus = 'COMPLETED' | 'ACTIVE' | 'LOCKED';

export interface ActivityModuleSession {
  id: string;
  name: string;
  description: string;
  seqNo: number;
  status: ActivityModuleStatus;
  totalSegments: number;
  completedSegments: number;
}

export interface ActivityModule {
  id: string;
  name: string;
  description: string;
  seqNo: number;
  status: ActivityModuleStatus;
  sessions: ActivityModuleSession[];
}

export interface GetActivityModulesResponse {
  modules: ActivityModule[];
}

export const useGetActivityModules = (activityId: string) =>
  useApiQuery({
    queryKey: createKey(`${Constants.GET_ACTIVITY_MODULES_BASE_URL}/${activityId}/modules`),
    queryFn: () =>
      makeRequest<GetActivityModulesResponse>(
        `${Constants.GET_ACTIVITY_MODULES_BASE_URL}/${activityId}/modules`,
        {},
        getCommonHeaders(),
        'get',
      ),
    enabled: Boolean(activityId),
    select: (data) => data.data,
  });

export type SegmentStatus = 'COMPLETED' | 'NOT_STARTED' | 'LOCKED';
export type SegmentType = 'VIDEO' | 'PDF' | 'OFFLINE_HTML' | 'ONLINE_HTML' | 'QUIZ';

export interface SegmentQuestionOption {
  type: string;
  value: string;
  isCorrect: boolean;
  evidenceId: string | null;
  explanation: string | null;
  evidenceType: string | null;
  explanationType: string | null;
}

export interface SegmentQuestionContent {
  questionText: string;
  questionMediaType: string | null;
  questionMedia: string | null;
  cmsQuestionId: string | null;
  metadata: {
    explanation: string | null;
    hints: unknown[];
    questionFormatData: {
      options: SegmentQuestionOption[];
    };
  };
}

export interface SegmentQuestion {
  id: string;
  seqNo: number;
  format: string;
  difficulty: number;
  type: number;
  mediumMapping: Record<string, SegmentQuestionContent>;
}

export interface SessionSegment {
  id: string;
  type: SegmentType;
  name: string;
  seqNo: number;
  status: SegmentStatus;
  path?: string;
  url?: string;
  score?: number | null;
  questions?: SegmentQuestion[];
  mediumPriority?: string[];
}

export interface GetSessionSegmentsResponse {
  segments: SessionSegment[];
}

export const useGetSessionSegments = (sessionId: string) =>
  useApiQuery({
    queryKey: createKey(`${Constants.GET_SESSION_SEGMENTS_BASE_URL}/${sessionId}/segments`),
    queryFn: () =>
      makeRequest<GetSessionSegmentsResponse>(
        `${Constants.GET_SESSION_SEGMENTS_BASE_URL}/${sessionId}/segments`,
        {},
        getCommonHeaders(),
        'get',
      ),
    enabled: Boolean(sessionId),
    select: (data) => data.data,
    gcTime: 0,
    staleTime: 0,
  });

export interface QuizQuestionStatPayload {
  questionId: string;
  seqNo: number;
  format: string;
  medium: string;
  isCorrect: boolean;
  answer: unknown;
  startTime: string;
  endTime: string;
  duration: number;
}

interface QuizSegmentProgressPayload {
  sessionId: string;
  segmentId: string;
  segmentType: 'QUIZ';
  startTime: string;
  endTime: string;
  duration: number;
  questionStats: QuizQuestionStatPayload[];
}

interface ModuleSegmentProgressPayload {
  sessionId: string;
  segmentId: string;
  segmentType: 'VIDEO' | 'PDF' | 'OFFLINE_HTML' | 'ONLINE_HTML';
  metadata: {
    viewTime: number;
    startTime: string;
    endTime: string;
    completion: number;
    pauseTime?: number;
    pageViewCount?: number;
    totalPageCount?: number;
  };
}

export type SegmentProgressPayload = QuizSegmentProgressPayload | ModuleSegmentProgressPayload;

export const useSubmitSegmentProgress = () =>
  useApiMutation({
    mutationFn: ({
      activityId,
      payload,
    }: {
      activityId: string;
      payload: SegmentProgressPayload;
    }) => {
      const accessToken = localStorage.getItem('accessToken');
      const headers = {
        ...getCommonHeaders(),
        Authorization: `Bearer ${accessToken}`,
      };
      return makeRequest(
        `${Constants.AUTH_BASE_URL}/student/activities/${activityId}/progress`,
        payload as unknown as Record<string, unknown>,
        headers,
      );
    },
  });

export const useGetActivities = () =>
  useApiQuery({
    queryKey: createKey(Constants.GET_ACTIVITIES_URL),
    queryFn: () => {
      const accessToken = localStorage.getItem('accessToken');
      const headers = {
        ...getCommonHeaders(),
        Authorization: `Bearer ${accessToken}`,
      };
      return makeRequest<GetActivitiesResponse>(Constants.GET_ACTIVITIES_URL, {}, headers);
    },
    select: (data) => data.data,
  });

export const useGetCertificates = (enabled = true) =>
  useApiQuery({
    queryKey: createKey(Constants.GET_CERTIFICATES_URL),
    queryFn: () =>
      makeRequest<GetCertificatesResponse>(
        Constants.GET_CERTIFICATES_URL,
        {},
        getCommonHeaders(),
        'get',
      ),
    enabled,
    select: (data) => data.data,
  });
